import React from "react";
import { NavLink, NavLinkProps } from "react-router-dom";
import { useDialogContext } from "@/components/DialogProvider";
import { useRoute, type RouteId } from "@/routes/getRoute";
import {
  FEATURE_PROTECTED_ROUTES,
  PARTNERSHIP_PROTECTED_ROUTES,
} from "@/routes/protectedRoutes";
import { UrlQueryObject } from "@/lib/UrlSerializers";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { AnalyticsEvent, useAnalytics } from "@/hooks/useAnalytics";

export interface ProtectedNavLinkProps extends Omit<NavLinkProps, "to"> {
  /** The route identifier */
  route: RouteId;
  /** Route parameters */
  params?: Record<string, string>;
  /** Query param parameters */
  query?: UrlQueryObject;
  /** Analytics event name to track on click */
  analyticsEvent?: AnalyticsEvent;
  /** Analytics properties to include with the event */
  analyticsProperties?: Record<string, unknown>;
}

/**
 * NavLink wrapper that provides automatic feature flag protection
 */
export const ProtectedNavLink = ({
  route,
  params = {},
  query = undefined,
  analyticsEvent,
  analyticsProperties,
  onClick,
  ref,
  ...navLinkProps
}: ProtectedNavLinkProps & {
  ref?: React.Ref<HTMLAnchorElement | null>;
}) => {
  const getRoute = useRoute();
  const { showFeatureDialog, showPermissionDialog } = useDialogContext();
  const { hasOrgFeature, hasMinOrgPartnershipLevel } = useOrgPermissions();
  const { trackEvent } = useAnalytics();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const partnerFeature = FEATURE_PROTECTED_ROUTES[route];
    const requiredPartnershipLevel = PARTNERSHIP_PROTECTED_ROUTES[route];

    if (partnerFeature) {
      const hasFeature = hasOrgFeature(partnerFeature);

      if (!hasFeature) {
        e.preventDefault();
        showFeatureDialog(partnerFeature);
        return;
      }
    }

    if (requiredPartnershipLevel) {
      const hasLevel = hasMinOrgPartnershipLevel(requiredPartnershipLevel);

      if (!hasLevel) {
        e.preventDefault();
        showPermissionDialog();
        return;
      }
    }

    if (analyticsEvent) {
      trackEvent(analyticsEvent, analyticsProperties);
    }

    onClick?.(e);
  };

  return (
    <NavLink
      to={getRoute(route, params, query)}
      ref={ref}
      onClick={handleClick}
      {...navLinkProps}
    />
  );
};
